<template>
    <v-row
        no-gutters
        style="position:relative; flex-wrap: nowrap;"
        :style="isMobile ? 'height: 27px' : ''"
        @wheel="onScroll"
    >
        <v-text-field
            v-if="!isMobile"
            :class="[
                'calendar-input',
                'toolbar-input',
                $route.name === 'dashboard' ? 'openable' : ''
            ]"
            style="max-width:240px;"
            solo
            readonly
            flat
            hide-details
            background-color="g2"
            @click="$route.name === 'dashboard' && $emit('update:calendar', !calendar)"
        >
            <template slot="prepend-inner">
                <div class="px-2">
                    <icon
                        icon="CalendarRange"
                        class-list="transition-ease-in-out"
                        :color="calendar ? 'main' : 'g4'"
                    />
                </div>
            </template>
            <template slot="label">
                <span
                    class="font-weight-medium body-2 transition-ease-in-out"
                    :class="calendar ? 'main--text' : 'g5--text'"
                >
                    {{ formatted_date_range }}
                </span>
            </template>
            <template slot="append">
                <icon-button
                    icon="ChevronLeft"
                    @click.stop="onCaretLeftClick"
                />
                <icon-button
                    icon="ChevronRight"
                    @click.stop="onCaretRightClick"
                    @keyup.left="onKeyDown"
                />
            </template>
        </v-text-field>
        <v-row
            v-else
            no-gutters
            align="center"
            style="flex-wrap: nowrap; white-space: nowrap; width: 130px;height: 30px;"
            justify="space-between"
        >
            <span class="subtitle-1 font-weight-medium main--text">
                {{ formatted_date_range }}
            </span>
            <div>
                <v-btn
                    icon
                    max-height="40px"
                    max-width="40px"
                    @click="onCaretLeftClick"
                >
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <div v-on="on">
                                <icon icon="ChevronLeft" />
                            </div>
                        </template>
                        <span>{{ $t("Previous day") }}</span>
                    </v-tooltip>
                </v-btn>
                <v-btn
                    icon
                    max-height="40px"
                    max-width="40px"
                    @click="onCaretRightClick"
                    @keyup.left="onKeyDown"
                >
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <div v-on="on">
                                <icon icon="ChevronRight" />
                            </div>
                        </template>
                        <span>{{ $t("Next day") }}</span>
                    </v-tooltip>
                </v-btn>
            </div>
        </v-row>
        <v-expand-x-transition>
            <div
                v-show="(!isMobile && calendar) || showTodayButton"
                class="overflow-hidden"
            >
                <v-btn
                    height="40px"
                    class="elevation-0 ml-2 text-none g5--text font-weight-medium"
                    style="letter-spacing: normal;"
                    color="g2"
                    @click="onTodayClick"
                >
                    {{ $t("Today") }}
                </v-btn>
            </div>
        </v-expand-x-transition>
    </v-row>
</template>

<script>
import { computed, onUnmounted, watch , onBeforeUnmount } from "@vue/composition-api";
import { $t } from "../../i18n";
import TaskModule from "../../store/modules/task";
import { parseSysDate, calcDateEquals, formatDateShort, formatSysDate } from "../../plugins/formatting";
import {eventBus} from '../../main'

export default {
    name: "AppToolbarCalendar",
    props: {
        isMobile: {
            type: Boolean
        },
        calendar: {
            type: Boolean
        },
        showTodayButton: {
            type: Boolean,
            default: false
        },
        range: {
            type: Number,
            default: 2
        },
        unit: {
            type: String,
            default: ""
        }
    },
    setup(props, ctx) {
        const {
            selected_date,
            selected_date_scope,
            startOfSelectedWeek,
            getEndOfSelectedWeek,
            task_dialog
        } = TaskModule();
        const endOfSelectedWeek = getEndOfSelectedWeek(props.range);
        const period_unit = computed(() => (props.unit ? props.unit : selected_date_scope.value));
        const formatted_date_range = computed(() => {
            let today = new Date();
            let selected = parseSysDate(selected_date.value);

            if (period_unit.value === "week") {
                if (
                    calcDateEquals(today, selected, period_unit.value) &&
                    selected.getWeekNumber() === today.getWeekNumber()
                ) {
                    return $t("This week");
                } else if (
                    calcDateEquals(today.calcDateAdd(1, period_unit.value), selected, period_unit.value) &&
                    selected.getWeekNumber() - today.getWeekNumber() === 1 &&
                    ctx.root.$vuetify.breakpoint.mdAndUp
                ) {
                    return $t("Next week");
                } else if (
                    calcDateEquals(today.calcDateAdd(-1, period_unit.value), selected, period_unit.value) &&
                    today.getWeekNumber() - selected.getWeekNumber() === 1 &&
                    ctx.root.$vuetify.breakpoint.mdAndUp
                ) {
                    return $t("Last week");
                } else {
                    return (
                        formatDateShort(startOfSelectedWeek.value) + " - " + formatDateShort(endOfSelectedWeek.value)
                    );
                }
            }

            if (period_unit.value === "day") {
                if (calcDateEquals(today, selected, period_unit.value)) {
                    return $t("Today");
                } else if (
                    calcDateEquals(today.calcDateAdd(1, period_unit.value), selected, period_unit.value) &&
                    ctx.root.$vuetify.breakpoint.mdAndUp
                ) {
                    return $t("Tomorrow");
                } else if (
                    calcDateEquals(today.calcDateAdd(-1, period_unit.value), selected, period_unit.value) &&
                    ctx.root.$vuetify.breakpoint.mdAndUp
                ) {
                    return $t("Yesterday");
                } else {
                    return formatDateShort(selected);
                }
            }
        });
        const navigateToNextPeriod = () => {
            selected_date.value = formatSysDate(parseSysDate(selected_date.value).calcDateAdd(1, period_unit.value));
        };
        const navigateToPreviousPeriod = () => {
            selected_date.value = formatSysDate(parseSysDate(selected_date.value).calcDateAdd(-1, period_unit.value));
        };
        const onCaretLeftClick = () => {
            navigateToPreviousPeriod();
        };
        const onCaretRightClick = () => {
            navigateToNextPeriod();
        };
        const onScroll = val => {
            if (val.deltaY > 0) {
                navigateToNextPeriod();
            }
            if (val.deltaY < 0) {
                navigateToPreviousPeriod();
            }
        };
        const onTodayClick = () => {
            selected_date.value = formatSysDate(new Date());
            selected_date_scope.value = "day";
        };

        watch(
            () => props.calendar,
            (val, prevVal) => {
                !prevVal && val && window.addEventListener("keydown", onKeyDown);
                prevVal && !val && window.removeEventListener("keydown", onKeyDown);
            }
        );

        watch(
            () => task_dialog.value.show,
            (val, prevVal) => {
                prevVal && val && window.addEventListener("keydown", onKeyDown);
                !prevVal && val && window.removeEventListener("keydown", onKeyDown);
            }
        );

        onUnmounted(() => {
            window.removeEventListener("keydown", onKeyDown);
        });
        onBeforeUnmount(() => {
            ctx.emit('update:calendar', false)
        })

        const onKeyDown = event => {
            if (event.keyCode === 39 || event.keyCode === 40) {
                event.preventDefault();
                navigateToNextPeriod();
            }
            if (event.keyCode === 37 || event.keyCode === 38) {
                event.preventDefault();
                navigateToPreviousPeriod();
            }
        };

        eventBus.$on("day_changed", (ev) => {
            if(selected_date_scope.value === 'day' && props.unit !== 'week') selected_date.value = formatSysDate(parseSysDate(selected_date.value).calcDateAdd(ev.days, period_unit.value))
        })

        return {
            onScroll,
            onCaretLeftClick,
            onCaretRightClick,
            formatted_date_range,
            onTodayClick,
            onKeyDown
        };
    }
};
</script>

<style lang="scss" scoped>
::v-deep .v-text-field > .v-input__control > .v-input__slot,
::v-deep .toolbar-input .v-icon {
    transition: none;
}
::v-deep .calendar-input.openable input,
::v-deep .calendar-input.openable .v-input__slot {
    cursor: pointer;
}
::v-deep .calendar-input .v-ripple__container {
    color: var(--v-white-base);
}
</style>
